import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";

/*
|--------------------------------------------------------------------------
| ZION PRAISE ACADEMY — OFFICIAL PAYMENT RECEIPT
|--------------------------------------------------------------------------
*/

const COLORS = {
  navy: "#102A43",
  royal: "#1D4ED8",
  blue: "#2563EB",
  sky: "#EFF6FF",
  gold: "#D6A63B",
  goldLight: "#FFF8E5",
  green: "#087F5B",
  greenLight: "#E8F7F0",
  red: "#C53030",
  redLight: "#FFF0F0",
  text: "#243447",
  gray: "#64748B",
  border: "#D9E2EC",
  light: "#F5F8FC",
  white: "#FFFFFF",
};

/*
|--------------------------------------------------------------------------
| RECEIPT STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  page: {
    paddingTop: 17,
    paddingBottom: 15,
    paddingHorizontal: 23,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    color: COLORS.text,
  },

  receiptContainer: {
    height: 350,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 5,
    paddingHorizontal: 11,
    paddingTop: 9,
    paddingBottom: 7,
    flexShrink: 0,
    backgroundColor: COLORS.white,
    position: "relative",
  },

  // Header
  header: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: COLORS.royal,
    paddingBottom: 7,
    marginBottom: 6,
  },

  logo: {
    width: 43,
    height: 43,
    objectFit: "contain",
    marginRight: 9,
  },

  schoolInfo: {
    flex: 1,
  },

  schoolName: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 3,
    textTransform: "uppercase",
  },

  schoolSubtitle: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.royal,
    marginBottom: 3,
    letterSpacing: 0.5,
  },

  contactText: {
    fontSize: 6.3,
    color: COLORS.gray,
    marginBottom: 2,
  },

  receiptBadge: {
    width: 100,
    paddingVertical: 7,
    paddingHorizontal: 5,
    backgroundColor: COLORS.navy,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },

  badgeLabel: {
    fontSize: 6,
    color: "#DCE8F5",
    marginBottom: 3,
    letterSpacing: 0.5,
  },

  badgeTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: COLORS.white,
  },

  // Receipt title
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },

  receiptTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    letterSpacing: 0.4,
  },

  copyLabel: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: COLORS.goldLight,
    borderWidth: 1,
    borderColor: COLORS.gold,
    borderRadius: 3,
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  // Receipt and payer information
  informationPanel: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 7,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 3,
    backgroundColor: COLORS.light,
    marginBottom: 6,
  },

  informationColumn: {
    width: "50%",
    paddingRight: 7,
  },

  informationColumnRight: {
    width: "50%",
    paddingLeft: 8,
    borderLeftWidth: 1,
    borderLeftColor: COLORS.border,
  },

  metaRow: {
    flexDirection: "row",
    marginBottom: 4,
    alignItems: "flex-start",
  },

  metaLabel: {
    width: 70,
    fontSize: 6.5,
    color: COLORS.gray,
  },

  metaValue: {
    flex: 1,
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.text,
  },

  // Payment ledger
  tableHeading: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 4,
    letterSpacing: 0.4,
  },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 2,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.navy,
    paddingVertical: 5,
    paddingHorizontal: 5,
  },

  tableHeaderText: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.white,
  },

  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 17,
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.border,
  },

  alternateRow: {
    backgroundColor: COLORS.light,
  },

  tableCell: {
    fontSize: 6.8,
    color: COLORS.text,
  },

  numberColumn: {
    width: "7%",
  },

  descriptionColumn: {
    width: "43%",
    paddingRight: 5,
  },

  amountColumn: {
    width: "25%",
    textAlign: "right",
    paddingRight: 7,
  },

  monthColumn: {
    width: "25%",
    textAlign: "right",
  },

  amountText: {
    fontFamily: "Helvetica-Bold",
    color: COLORS.green,
  },

  // Summary
  bottomSection: {
    flexDirection: "row",
    alignItems: "stretch",
    marginTop: 6,
  },

  paymentNote: {
    flex: 1,
    paddingRight: 9,
    justifyContent: "center",
  },

  paymentNoteTitle: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 4,
  },

  paymentNoteText: {
    fontSize: 6.3,
    lineHeight: 1.35,
    color: COLORS.gray,
  },

  summary: {
    width: "43%",
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 3,
    paddingHorizontal: 6,
    paddingVertical: 4,
  },

  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.border,
  },

  summaryLabel: {
    fontSize: 6.8,
    color: COLORS.gray,
  },

  summaryValue: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  paidValue: {
    color: COLORS.green,
  },

  balanceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 5,
    paddingHorizontal: 5,
    marginTop: 4,
    backgroundColor: COLORS.sky,
    borderLeftWidth: 2,
    borderLeftColor: COLORS.royal,
    borderRadius: 2,
  },

  balanceLabel: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  balanceValue: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.royal,
  },

  // Signatures
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingHorizontal: 4,
  },

  signatureBox: {
    width: "36%",
    borderTopWidth: 1,
    borderTopColor: COLORS.gray,
    paddingTop: 4,
    alignItems: "center",
  },

  signatureText: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  signatureSubtitle: {
    fontSize: 5.8,
    color: COLORS.gray,
    marginTop: 2,
  },

  receiptNote: {
    marginTop: 6,
    paddingTop: 4,
    borderTopWidth: 0.6,
    borderTopColor: COLORS.border,
    fontSize: 5.8,
    color: COLORS.gray,
    textAlign: "center",
  },

  // Two-copy separator
  separator: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 7,
  },

  separatorLine: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    borderStyle: "dashed",
  },

  separatorLabel: {
    paddingHorizontal: 9,
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    color: COLORS.gray,
  },
});

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const formatAmount = (value) => {
  const number = Number(value || 0);

  return (Number.isFinite(number) ? number : 0).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const capitalize = (value) => {
  if (!value) return "N/A";

  const text = String(value);

  return text.charAt(0).toUpperCase() + text.slice(1);
};

/*
|--------------------------------------------------------------------------
| SINGLE RECEIPT COPY
|--------------------------------------------------------------------------
*/

const PaymentReceipt = ({
  payment,
  paymentHistory = [],
  copyType,
}) => {
  const records = Array.isArray(paymentHistory)
    ? paymentHistory
    : [];

  const totalPaid = records.reduce(
    (total, item) => total + (Number(item.amount_paid) || 0),
    0
  );

  const totalAmount = records.reduce(
    (total, item) => total + (Number(item.total_amount) || 0),
    0
  );

  const totalBalance = Math.max(totalAmount - totalPaid, 0);

  return (
    <View style={styles.receiptContainer} wrap={false}>
      {/* SCHOOL HEADER */}

      <View style={styles.header}>
        <Image src={schoolLogo} style={styles.logo} />

        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>
            Zion Praise Academy
          </Text>

          <Text style={styles.schoolSubtitle}>
            CRM SCHOOL SYSTEM
          </Text>

          <Text style={styles.contactText}>
            Cowfield 1st Junction, Duport Road, Paynesville, Monrovia, Liberia
          </Text>

          <Text style={styles.contactText}>
            Contact: +231-770-235-661 / +231-886-334-877
          </Text>

          <Text style={styles.contactText}>
            Student Accounts & Finance Department
          </Text>
        </View>

        <View style={styles.receiptBadge}>
          <Text style={styles.badgeLabel}>
            OFFICIAL RECEIPT
          </Text>

          <Text style={styles.badgeTitle}>
            PAYMENT
          </Text>
        </View>
      </View>

      {/* RECEIPT TITLE */}

      <View style={styles.titleRow}>
        <Text style={styles.receiptTitle}>
          OTHER FEES PAYMENT RECEIPT
        </Text>

        <Text style={styles.copyLabel}>
          {copyType}
        </Text>
      </View>

      {/* PAYER AND TRANSACTION DETAILS */}

      <View style={styles.informationPanel}>
        <View style={styles.informationColumn}>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Receipt No.
            </Text>

            <Text style={styles.metaValue}>
              OFP-{payment?.id ?? "N/A"}
            </Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Payer
            </Text>

            <Text style={styles.metaValue}>
              {payment?.payer_name || "N/A"}
            </Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Student
            </Text>

            <Text style={styles.metaValue}>
              {payment?.student_name || "Manual Payer"}
            </Text>
          </View>
        </View>

        <View style={styles.informationColumnRight}>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Academic Year
            </Text>

            <Text style={styles.metaValue}>
              {payment?.academic_year_name || "N/A"}
            </Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Class
            </Text>

            <Text style={styles.metaValue}>
              {payment?.student_class_name || "N/A"}
            </Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>
              Payment Date
            </Text>

            <Text style={styles.metaValue}>
              {payment?.date || "N/A"}
            </Text>
          </View>
        </View>
      </View>

      {/* PAYMENT DETAILS TABLE */}

      <Text style={styles.tableHeading}>
        PAYMENT DETAILS
      </Text>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text
            style={[
              styles.tableHeaderText,
              styles.numberColumn,
            ]}
          >
            No.
          </Text>

          <Text
            style={[
              styles.tableHeaderText,
              styles.descriptionColumn,
            ]}
          >
            PAYMENT DESCRIPTION
          </Text>

          <Text
            style={[
              styles.tableHeaderText,
              styles.amountColumn,
            ]}
          >
            AMOUNT ($)
          </Text>

          <Text
            style={[
              styles.tableHeaderText,
              styles.monthColumn,
            ]}
          >
            MONTH
          </Text>
        </View>

        {records.length > 0 ? (
          records.map((item, index) => (
            <View
              key={item.id ?? `payment-${index}`}
              style={[
                styles.tableRow,
                index % 2 === 1 ? styles.alternateRow : {},
              ]}
              wrap={false}
            >
              <Text
                style={[
                  styles.tableCell,
                  styles.numberColumn,
                ]}
              >
                {index + 1}
              </Text>

              <Text
                style={[
                  styles.tableCell,
                  styles.descriptionColumn,
                ]}
              >
                {item.fee_type_name || "Other Fees"}
              </Text>

              <Text
                style={[
                  styles.tableCell,
                  styles.amountColumn,
                  styles.amountText,
                ]}
              >
                {formatAmount(item.amount_paid)}
              </Text>

              <Text
                style={[
                  styles.tableCell,
                  styles.monthColumn,
                ]}
              >
                {capitalize(item.month)}
              </Text>
            </View>
          ))
        ) : (
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>
              No payment details available.
            </Text>
          </View>
        )}
      </View>

      {/* RECEIPT NOTE AND FINANCIAL SUMMARY */}

      <View style={styles.bottomSection}>
        <View style={styles.paymentNote}>
          <Text style={styles.paymentNoteTitle}>
            PAYMENT ACKNOWLEDGEMENT
          </Text>

          <Text style={styles.paymentNoteText}>
            This receipt acknowledges the payment entries listed above.
            Please retain this copy for your records and contact the
            Finance Department if any information requires verification.
          </Text>
        </View>

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Total Amount
            </Text>

            <Text style={styles.summaryValue}>
              ${formatAmount(totalAmount)}
            </Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Total Paid
            </Text>

            <Text
              style={[
                styles.summaryValue,
                styles.paidValue,
              ]}
            >
              ${formatAmount(totalPaid)}
            </Text>
          </View>

          <View style={styles.balanceRow}>
            <Text style={styles.balanceLabel}>
              BALANCE
            </Text>

            <Text style={styles.balanceValue}>
              ${formatAmount(totalBalance)}
            </Text>
          </View>
        </View>
      </View>

      {/* SIGNATURES */}

      <View style={styles.footer}>
        <View style={styles.signatureBox}>
          <Text style={styles.signatureText}>
            Payer's Signature
          </Text>

          <Text style={styles.signatureSubtitle}>
            Name / Date
          </Text>
        </View>

        <View style={styles.signatureBox}>
          <Text style={styles.signatureText}>
            Authorized Signature
          </Text>

          <Text style={styles.signatureSubtitle}>
            Finance Department
          </Text>
        </View>
      </View>

      <Text style={styles.receiptNote}>
        Computer-generated receipt | Zion Praise Academy - CRM School System
      </Text>
    </View>
  );
};

/*
|--------------------------------------------------------------------------
| TWO-COPY RECEIPT PDF
|--------------------------------------------------------------------------
*/

const OtherFeePaymentRecieptPDF = ({
  payment,
  paymentHistory = [],
}) => {
  return (
    <Document
      title={`Other Fees Receipt - OFP-${payment?.id ?? "N/A"}`}
      author="Zion Praise Academy - CRM School System"
      subject="Other Fees Payment Receipt"
    >
      <Page
        size="A4"
        orientation="portrait"
        style={styles.page}
        wrap={false}
      >
        <PaymentReceipt
          payment={payment}
          paymentHistory={paymentHistory}
          copyType="PAYER OR STUDENT COPY"
        />

        <View style={styles.separator}>
          <View style={styles.separatorLine} />

          <Text style={styles.separatorLabel}>
            CUT HERE
          </Text>

          <View style={styles.separatorLine} />
        </View>

        <PaymentReceipt
          payment={payment}
          paymentHistory={paymentHistory}
          copyType="ADMINISTRATION COPY"
        />
      </Page>
    </Document>
  );
};

export default OtherFeePaymentRecieptPDF;